import { FC, useMemo, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'
import debounce from 'lodash/debounce'

import { Product } from '../../utils/types/Product'
import { getMediumPreview, imageLoader } from '../../utils/images'
import { getProductImageAltText } from '../../lib/text'
import useUrls from '../../services/useUrls'
import { findCommonParts, getPathName, getUniquePart } from '../../lib/product'
import Carousel from '../Layouts/Carousel'
import selectProductColorVariant from '../../utils/analytics/selectProductColorVariant'
import useGetLocalizedProductUrl from '../SingleProduct/hooks/useGetLocalizedProductUrl'
import useIsOnMobile from '../../services/useIsOnMobile'
import { ScreenSize } from '../../utils/constants'

import variantStyles from '../../styles/ProductColorVariants.module.less'

interface RelatedProductsProps {
  product: Product
}

const ProductColorVariants: FC<RelatedProductsProps> = ({ product }) => {
  const { t } = useTranslation('products')
  const { urlT } = useUrls()
  const [hoveredProduct, setHoveredProduct] = useState<Product | null>(null)
  const getLocalizedProductUrl = useGetLocalizedProductUrl()
  const isOnMobile = useIsOnMobile(ScreenSize.md)
  const { relatedProducts = [] } = product

  // Combine current product and related products
  const allProducts = useMemo(() => {
    // Combine current product and related products
    const products = [product, ...(relatedProducts || [])]
    // Sort all products by id (as string)
    return products.sort((a, b) => Number(a.id) - Number(b.id))
  }, [product, relatedProducts])

  // Find common parts across all products
  const commonParts = useMemo(() => findCommonParts(allProducts), [allProducts])

  const renderProductCards = (products: Product[]) => (
    <Carousel
      gap={5}
      hideArrowsFade
      rightIconPadding={isOnMobile ? 20 : 0}
    >
      {products.map((productItem, index) => {
        const productUrl = urlT(getPathName(productItem, t))
        const imageAltText = getProductImageAltText(productItem)
        const isCurrentProduct = productItem.id === product.id

        const handleVariantClick = debounce(() => {
          if (!isCurrentProduct) {
            selectProductColorVariant({
              selectedVariantProduct: productItem,
              currentProduct: product,
              selectedVariantType: 'color',
              variantValue: productItem.attribute.find((attribute) => attribute['attribute.type'] === 'color')?.['attribute.code'] || '',
              totalVariantsAvailable: products.length,
              variantPosition: index,
              getLocalizedProductUrl,
            })
          }
        }, 1000)

        return (
          <Link
            href={productUrl}
            prefetch
            key={productItem.id}
            className={`${variantStyles['variant-card']} ${isCurrentProduct ? variantStyles['current-variant'] : ''}`}
            onMouseEnter={() => setHoveredProduct(productItem)}
            onMouseLeave={() => setHoveredProduct(null)}
            onClick={handleVariantClick}
          >
            <Image
              loader={imageLoader}
              src={getMediumPreview(productItem?.media?.[0])}
              alt={imageAltText}
              className={variantStyles['variant-image']}
              width={70}
              height={70}
            />
          </Link>
        )
      })}
    </Carousel>
  )

  if (!relatedProducts?.length) {
    return null
  }

  return (
    <div className={variantStyles['variants-section']}>
      {allProducts?.length > 0 && (
        <>
          <div className={variantStyles['variant-label']}>
            {hoveredProduct ? (
              <>
                {t('Alternative')}
                :
                {' '}
                <b>{getUniquePart(hoveredProduct['product.label'], commonParts)}</b>
              </>
            ) : (
              t('View_alternative', { count: allProducts.length })
            )}
          </div>
          <div className={variantStyles['variants-carousel']}>
            {renderProductCards(allProducts)}
          </div>
        </>
      )}
    </div>
  )
}

export default ProductColorVariants
